import React from 'react';
import { AgentConfig } from '../../../shared/types/agent';

interface AgentListProps {
  agents: AgentConfig[];
  selectedAgent: AgentConfig | null;
  onSelectAgent: (agent: AgentConfig) => void;
  onEditAgent?: (agent: AgentConfig) => void;
  onDeleteAgent: (agentId: string) => void;
  onUpdateAgent?: (agentId: string, updates: Partial<AgentConfig>) => void;
  loading: boolean;
  error?: string | null;
  className?: string;
}

export const AgentList: React.FC<AgentListProps> = ({
  agents,
  selectedAgent,
  onSelectAgent,
  onEditAgent,
  onDeleteAgent,
  onUpdateAgent,
  loading,
  error,
  className = '',
}) => {
  if (loading && agents.length === 0) {
    return (
      <div className="p-4 text-center text-gray-400">
        <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
        Loading agents...
      </div>
    );
  }

  if (agents.length === 0) {
    return (
      <div className="p-4 text-center text-gray-400">
        <div className="text-4xl mb-4">🤖</div>
        <p>No agents created yet</p>
        <p className="text-sm mt-2">Create your first agent to get started</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-2">
      {agents.map((agent) => (
        <div
          key={agent.id}
          className={`p-3 rounded-lg border cursor-pointer transition-colors ${
            selectedAgent?.id === agent.id
              ? 'bg-blue-900/20 border-blue-500/50'
              : 'bg-gray-800/50 border-gray-700 hover:bg-gray-800 hover:border-gray-600'
          }`}
          onClick={() => onSelectAgent(agent)}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-medium text-white truncate">{agent.name}</h3>
                <span className={`px-2 py-0.5 text-xs rounded ${
                  agent.isActive 
                    ? 'bg-green-900/30 text-green-300' 
                    : 'bg-gray-700 text-gray-300'
                }`}>
                  {agent.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className="text-sm text-gray-400 mb-2 line-clamp-2">
                {agent.description || agent.objective}
              </p>
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span className="capitalize">{agent.type}</span>
                <span>{agent.tools.length} tools</span>
                <span>{agent.chatSessions.length} sessions</span>
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDeleteAgent(agent.id);
              }}
              className="ml-2 p-1 text-gray-400 hover:text-red-400 transition-colors"
              title="Delete agent"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};
